import { Fragment, useContext, useState, type ReactNode } from 'react'
import { AddOnsContext, useEvaluatorPack } from '../addons'
import { helpFor, ROLE_COLUMNS, ROLE_MATRIX, type HelpTopic } from '../help'
import { AddOnFootnote, EvaluatorName, mentionsEvaluator, withStars } from './AddOn'
import { ProductName } from './Brand'

/**
 * The manual, on the screen it describes — TECHNICAL_DESIGN.md §4.
 *
 * Two surfaces over one body of text: a panel for the screen somebody is on,
 * and the whole manual when they want to read round it. Written once in
 * `help.ts` and rendered twice, so the panel cannot say something the manual
 * does not.
 *
 * **On every screen, for every role**, because the roles here are not a ladder —
 * an administrator cannot approve a limit and an approver cannot administer the
 * appliance — so *"you don't have permission"* is a sentence people meet
 * legitimately, and every topic says who may do what.
 */

/** The words searched for, lower-cased. A topic matches when it says every one. */
function termsOf(query: string): string[] {
  return query.toLowerCase().split(/\s+/).filter(Boolean)
}

/** Everything a topic says, title first, in the order it says it. */
function passages(topic: HelpTopic): string[] {
  return [
    topic.title,
    topic.purpose,
    ...(topic.before ?? []),
    ...(topic.steps ?? []).flatMap((step) => [step.what, step.detail]),
    topic.workflow ?? '',
    topic.roles,
    ...(topic.notes ?? []),
  ].filter(Boolean)
}

function matches(topic: HelpTopic, terms: string[]): boolean {
  const text = passages(topic).join(' ').toLowerCase()
  return terms.every((term) => text.includes(term))
}

/**
 * The line under a search result: the first passage past the title that
 * mentions a term, cut down to the words around it — so a result found in the
 * body says *why* it was found, which a bare title cannot.
 */
function snippet(topic: HelpTopic, terms: string[]): string | null {
  for (const passage of passages(topic).slice(1)) {
    const lower = passage.toLowerCase()
    const found = terms.map((term) => lower.indexOf(term)).filter((at) => at >= 0)
    if (found.length === 0) continue
    const first = Math.min(...found)
    const start = Math.max(0, first - 30)
    const end = Math.min(passage.length, first + 70)
    return `${start > 0 ? '…' : ''}${passage.slice(start, end).trim()}${end < passage.length ? '…' : ''}`
  }
  return null
}

/** Text with the searched-for words picked out. */
function Marked({ text, terms }: { text: string; terms: string[] }) {
  if (terms.length === 0) return <>{text}</>
  const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  // Split on a capturing group, so the matches land at the odd indices.
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'gi'))
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <mark key={i}>{part}</mark> : <Fragment key={i}>{part}</Fragment>,
      )}
    </>
  )
}

const ROLE_NAMES: Record<string, string> = {
  viewer: 'Viewer',
  author: 'Author',
  approver: 'Approver',
  admin: 'Administrator',
}

/**
 * Roles across, what each may do down. The sentence above it says who does what in
 * general; the matrix answers the question somebody actually brings to the People
 * screen — *which role do I give this person so they can do that*.
 */
/** The matrix's rows this installation runs — a pack's rows only where it is licensed. */
function useMatrixRows() {
  const addOns = useContext(AddOnsContext)
  return ROLE_MATRIX.filter((row) => !row.pack || addOns.includes(row.pack))
}

function RoleMatrix({ render }: { render: (text: string) => ReactNode }) {
  const rows = useMatrixRows()
  return (
    <div className="table-scroll">
      <table className="grid role-matrix" data-testid="role-matrix">
        <thead>
          <tr>
            <th scope="col">What somebody can do</th>
            {ROLE_COLUMNS.map((role) => (
              <th key={role} scope="col" className="role-cell">
                {ROLE_NAMES[role]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.capability}>
              <th scope="row">{render(row.task)}</th>
              {ROLE_COLUMNS.map((role) => (
                <td key={role} className="role-cell">
                  {row.roles.includes(role) ? (
                    <span className="role-yes" aria-label="yes">
                      ✓
                    </span>
                  ) : (
                    <span className="role-no" aria-label="no">
                      –
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Topic({
  topic,
  heading = 'h3',
  terms = [],
}: {
  topic: HelpTopic
  heading?: 'h2' | 'h3'
  terms?: string[]
}) {
  const Heading = heading
  const matrixRows = useMatrixRows()
  // Searched-for words picked out, and the add-on pack's name starred (decision 30).
  const m = (text: string) => withStars(text, (piece) => <Marked text={piece} terms={terms} />)
  return (
    <article className="help-topic">
      <Heading>{m(topic.title)}</Heading>
      <p>{m(topic.purpose)}</p>

      {topic.before && topic.before.length > 0 && (
        <>
          <h4>What has to exist first</h4>
          <ul>
            {topic.before.map((item) => (
              <li key={item}>{m(item)}</li>
            ))}
          </ul>
        </>
      )}

      {topic.steps && topic.steps.length > 0 && (
        <>
          <h4>How to</h4>
          <ol className="help-steps">
            {topic.steps.map((step) => (
              <li key={step.what}>
                <strong>{m(step.what)}</strong>
                <span>{m(step.detail)}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      {topic.workflow && (
        <>
          <h4>Where it sits</h4>
          <p>{m(topic.workflow)}</p>
        </>
      )}

      <h4>Who can do what</h4>
      <p>{m(topic.roles)}</p>
      {topic.roleMatrix && <RoleMatrix render={m} />}

      {topic.notes && topic.notes.length > 0 && (
        <>
          <h4>Worth knowing</h4>
          <ul>
            {topic.notes.map((note) => (
              <li key={note}>{m(note)}</li>
            ))}
          </ul>
        </>
      )}
      {(passages(topic).some(mentionsEvaluator) ||
        (topic.roleMatrix && matrixRows.some((row) => mentionsEvaluator(row.task)))) && (
        <AddOnFootnote />
      )}
    </article>
  )
}

/** The panel for one screen, opened by the help control beside the tabs. */
export function HelpPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const topic = helpFor(id)
  const pack = useEvaluatorPack()
  return (
    <aside className="help-panel" role="dialog" aria-label="Help">
      <div className="help-head">
        <strong>Help</strong>
        <button className="ghost" onClick={onClose} aria-label="Close help">
          ✕
        </button>
      </div>
      {topic ? (
        <Topic topic={topic} />
      ) : (
        <p className="note">There is no written help for this screen yet.</p>
      )}
      {/* Where limits go next, and what the add-on pack adds (decision 27). */}
      <p className="note">
        GUARD keeps every limit current and exports it to your order management
        or compliance system, where pre-trade checks run.{' '}
        {pack ? (
          <>
            The <EvaluatorName /> on this installation evaluates the same limits
            against your holdings.
          </>
        ) : (
          <>
            The <EvaluatorName /> add-on pack evaluates the same limits against your
            holdings inside GUARD.
          </>
        )}
        <AddOnFootnote />
      </p>
    </aside>
  )
}

/**
 * The whole manual, reached from the account menu.
 *
 * The screens, grouped by where they sit in the work rather than listed
 * alphabetically. Fourteen topics stacked on one page is a page nobody reads to
 * the end of; fourteen tabs in a row is a row nobody reads either. Grouped and
 * down the side, the index is also the answer to *what is the shape of this
 * product* — which is the question somebody opening the manual actually has.
 */
const GROUPS: { title: string; hint: string; ids: string[] }[] = [
  {
    title: 'Setting up',
    hint: 'Once, in this order.',
    ids: ['users', 'desks', 'funds', 'groups', 'dictionary', 'dictionary-reconcile', 'connector', 'operations'],
  },
  {
    title: 'Bringing guidelines in',
    hint: 'From a workbook, from the mandate itself, or from a client’s mail.',
    ids: ['migration', 'documents', 'clauses', 'submissions', 'approvals'],
  },
  {
    title: 'Keeping them current',
    hint: 'The clocks that stop a record going quietly stale, and the tidying.',
    ids: ['reviews', 'attest', 'membership', 'promotions'],
  },
  {
    title: 'Reading them',
    hint: 'What most people are here to do — and the file that leaves.',
    ids: ['lookup', 'compare', 'search', 'excel', 'export', 'export-verify', 'export-pack'],
  },
  {
    title: 'Evaluating holdings',
    hint: 'Holdings loaded, judged against the limits, and read fund by fund.',
    ids: ['verdicts', 'agreement', 'whatif', 'cure', 'calendars', 'benchmarks', 'holdings', 'holdings-feed'],
  },
  {
    title: 'Accounting for it',
    hint: 'Who did what here, and when.',
    ids: ['audit', 'job-history'],
  },
]

export function HelpBook() {
  const [chosen, setChosen] = useState<string>('overview')
  const pack = useEvaluatorPack()
  const addOns = useContext(AddOnsContext)
  const topic = helpFor(chosen)
  // Which sections of the index are open, like a documentation site's menu. The
  // first starts open, since setting up is where somebody new begins.
  const [open, setOpen] = useState<Set<string>>(() => new Set([GROUPS[0]!.title]))
  const [query, setQuery] = useState('')
  const terms = termsOf(query)
  const searching = terms.length > 0

  const toggle = (title: string) =>
    setOpen((was) => {
      const next = new Set(was)
      if (next.has(title)) next.delete(title)
      else next.add(title)
      return next
    })
  // Choosing a topic opens its section, so clearing a search leaves the topic on
  // screen findable in the index rather than folded away.
  const select = (id: string) => {
    setChosen(id)
    const home = GROUPS.find((group) => group.ids.includes(id))
    if (home) setOpen((was) => new Set(was).add(home.title))
  }

  // While searching, every section with a match is open and the rest are hidden. A
  // pack's topics are left out where the pack is not licensed, as its screens are
  // left out of the menus, and a section left with nothing is not drawn.
  const sections = GROUPS.map((group) => {
    const entries = group.ids
      .map((id) => [id, helpFor(id)] as const)
      .filter((pair): pair is readonly [string, HelpTopic] => Boolean(pair[1]))
      .filter(([, e]) => !e.pack || addOns.includes(e.pack))
    return { ...group, entries: searching ? entries.filter(([, e]) => matches(e, terms)) : entries }
  }).filter((group) => group.entries.length > 0)

  return (
    <section className="help-book">
      <header className="page-head">
        <h2>Help</h2>
        <p className="note">
          Every screen in <ProductName />: what it is for, what has to exist before it
          works, how to use it, and who is allowed to.
        </p>
      </header>

      <div className="help-layout">
        <nav className="help-index" aria-label="Help topics">
          <div className="help-search">
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="m13 13 4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder="Search help"
              aria-label="Search help"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setQuery('')
              }}
            />
          </div>
          <div role="tablist" aria-orientation="vertical" aria-label="Start">
            <button
              role="tab"
              className="help-index-item help-index-start"
              aria-selected={chosen === 'overview'}
              onClick={() => setChosen('overview')}
            >
              Start here
            </button>
          </div>
          {sections.map((group) => {
            const expanded = searching || open.has(group.title)
            const classes = [
              'help-group',
              expanded && 'open',
              group.ids.includes(chosen) && 'current',
            ]
            return (
              <div key={group.title} className={classes.filter(Boolean).join(' ')}>
                <button
                  className="help-group-head"
                  aria-expanded={expanded}
                  title={group.hint}
                  onClick={() => {
                    if (!searching) toggle(group.title)
                  }}
                >
                  <span className="help-group-title">{group.title}</span>
                  <span className="help-group-count">{group.entries.length}</span>
                  <span className="chevron" aria-hidden />
                </button>
                {expanded && (
                  <div
                    className="help-group-items"
                    role="tablist"
                    aria-orientation="vertical"
                    aria-label={group.title}
                  >
                    {group.entries.map(([id, entry]) => {
                      const why = searching ? snippet(entry, terms) : null
                      return (
                        <button
                          key={id}
                          role="tab"
                          className="help-index-item"
                          aria-selected={chosen === id}
                          onClick={() => select(id)}
                        >
                          <Marked text={entry.title} terms={terms} />
                          {why && (
                            <span className="help-index-snippet">
                              <Marked text={why} terms={terms} />
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          {searching && sections.length === 0 && (
            <p className="help-search-empty">
              No topic mentions “{query.trim()}”. Try the name of a screen, or a word
              such as approve, export or attest.
            </p>
          )}
        </nav>

        <div className="help-content" role="tabpanel">
          {chosen === 'overview' ? (
            <div className="card">
              <h2>Start here</h2>
              <p>
                <strong>
                  <ProductName /> is where your limits live, and how they reach your OMS.
                </strong>{' '}
                Every limit in force, where each came from, and the export your order
                management or compliance system loads for pre-trade checks.{' '}
                {pack ? (
                  <>
                    The <EvaluatorName strong={false} /> on this installation evaluates
                    them against your holdings as well.
                  </>
                ) : (
                  <>
                    Add the <EvaluatorName strong={false} /> pack to evaluate them
                    against your holdings inside GUARD.
                  </>
                )}
              </p>

              <h4>The shape of the work</h4>
              <ol className="help-steps">
                <li>
                  <strong>Define the vocabulary</strong>
                  <span>
                    A rule can only be written in terms this appliance knows.
                    Starter markets fills that in for a mandate of your kind.
                  </span>
                </li>
                <li>
                  <strong>Bring the guidelines in</strong>
                  <span>
                    From an existing workbook, from the mandate document clause by
                    clause, or from a client’s mail. All three produce proposals.
                  </span>
                </li>
                <li>
                  <strong>An approver puts them into force</strong>
                  <span>
                    The one step that makes a limit binding. Nothing else in the
                    product can.
                  </span>
                </li>
                <li>
                  <strong>Confirm them on their cycle, and attest</strong>
                  <span>
                    The spreadsheet this replaces was not wrong because it was a
                    spreadsheet — it was wrong because nothing made anybody
                    confirm it.
                  </span>
                </li>
                <li>
                  <strong>Export to your compliance engine</strong>
                  <span>
                    Your OMS runs pre-trade checks against the file. With the{' '}
                    <EvaluatorName strong={false} /> pack, GUARD evaluates your holdings
                    against them too.
                  </span>
                </li>
              </ol>

              <h4>Who does what</h4>
              <p className="note">
                The roles are not a ladder. An administrator runs the appliance
                and cannot move a limit; an approver moves limits and cannot
                administer the appliance. That separation is the point of having
                roles at all, so <em>you do not have permission</em> is a sentence
                people meet legitimately here.
              </p>
              <AddOnFootnote />
            </div>
          ) : topic ? (
            <div className="card">
              <Topic topic={topic} heading="h2" terms={terms} />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
